import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { motion } from 'framer-motion';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import StarIcon from '@mui/icons-material/Star';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import DiscountIcon from '@mui/icons-material/Discount';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

import ProductCard from '../../../components/ProductCard';
import { useProduct } from '../../../context/ProductContext';
import { Product, Tag } from '../../../models/Product';
import Loading from '../../../components/global/loading/Loading';
import { useConfig } from '../../../utils/useConfig';

const tagIcons = {
  [Tag.Recommended]: <StarIcon />,
  [Tag.Bestseller]: <LocalFireDepartmentIcon />,
  [Tag.New]: <NewReleasesIcon />,
  [Tag.Sale]: <DiscountIcon />,
};

function Recommended() {
  const { getProducts } = useProduct();
  const config = useConfig();
  const mainColor =
    config.theme.mainColor || config.theme.colors.primary || '#3B82F6';

  const [recommended, setRecommended] = useState<Product[]>([]);
  const [activeTag, setActiveTag] = useState<Tag>(Tag.Recommended);
  const [isLoading, setIsLoading] = useState(true);

  // Pobieranie produktów na podstawie aktywnego tagu
  useEffect(() => {
    const fetchProducts = async (tag: Tag) => {
      setIsLoading(true);
      try {
        const response = await getProducts({
          draft: false,
          page: 1,
          limit: 8, // Pokazujemy maksymalnie 8 produktów
          tag: tag,
        });
        setRecommended(response.products);
      } catch (e) {
        console.error('Error fetching products:', e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts(activeTag);
  }, [activeTag, getProducts]);

  type ColorKey = 'amber' | 'red' | 'blue' | 'green';

  const tagDetails = {
    [Tag.Recommended]: {
      label: 'Polecane',
      color: 'amber' as ColorKey,
      description: 'Wybrane przez naszych specjalistów',
      bgGradient: 'from-amber-50 to-white',
    },
    [Tag.Bestseller]: {
      label: 'Bestsellery',
      color: 'red' as ColorKey,
      description: 'Najpopularniejsze wybory',
      bgGradient: 'from-red-50 to-white',
    },
    [Tag.New]: {
      label: 'Nowości',
      color: 'blue' as ColorKey,
      description: 'Sprawdź najnowsze produkty',
      bgGradient: 'from-blue-50 to-white',
    },
    [Tag.Sale]: {
      label: 'Promocje',
      color: 'green' as ColorKey,
      description: 'Atrakcyjne oferty specjalne',
      bgGradient: 'from-green-50 to-white',
    },
  };

  const tagColorClasses = {
    amber: {
      bg: 'bg-amber-100',
      text: 'text-amber-600',
      border: 'border-amber-200',
      shadow: 'shadow-amber-100/50',
      highlight: 'bg-amber-500',
      hover: 'hover:bg-amber-200',
      ring: 'ring-amber-200',
    },
    red: {
      bg: 'bg-red-100',
      text: 'text-red-600',
      border: 'border-red-200',
      shadow: 'shadow-red-100/50',
      highlight: 'bg-red-500',
      hover: 'hover:bg-red-200',
      ring: 'ring-red-200',
    },
    blue: {
      bg: 'bg-blue-100',
      text: 'text-blue-600',
      border: 'border-blue-200',
      shadow: 'shadow-blue-100/50',
      highlight: 'bg-blue-500',
      hover: 'hover:bg-blue-200',
      ring: 'ring-blue-200',
    },
    green: {
      bg: 'bg-green-100',
      text: 'text-green-600',
      border: 'border-green-200',
      shadow: 'shadow-green-100/50',
      highlight: 'bg-green-500',
      hover: 'hover:bg-green-200',
      ring: 'ring-green-200',
    },
  };

  const currentTagColors = tagColorClasses[tagDetails[activeTag].color];

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut',
      },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div
      className={`w-full py-10 md:py-16 px-4`}
      style={{
        background: `linear-gradient(to bottom, ${mainColor}05, white)`,
      }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Nagłówek sekcji */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-black to-primary">
              {tagDetails[activeTag].label} produkty
            </span>
          </h2>
          <div
            className={`mx-auto w-16 md:w-20 h-1.5 rounded-full mb-3 md:mb-4 ${currentTagColors.highlight}`}
          ></div>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
            {tagDetails[activeTag].description}
          </p>
        </motion.div>

        {/* Zakładki do przełączania kategorii produktów - nowoczesna wersja */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
          className="mb-8 md:mb-10 flex justify-center"
        >
          <div className="flex flex-wrap justify-center items-center gap-2 md:gap-3 p-1.5 bg-gray-100/80 backdrop-blur-sm rounded-full">
            {Object.values(Tag).map((tag) => {
              const isActive = activeTag === tag;
              const tagColor = tagColorClasses[tagDetails[tag].color];

              return (
                <motion.button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`flex items-center px-4 py-2.5 rounded-full transition-all duration-300 text-sm md:text-base ${
                    isActive
                      ? `${tagColor.bg} ${tagColor.text} font-medium shadow-md ${tagColor.shadow}`
                      : 'text-gray-600 hover:bg-white hover:shadow-sm'
                  }`}
                  whileHover={{ scale: isActive ? 1 : 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="mr-2">{tagIcons[tag]}</span>
                  <span>{tagDetails[tag].label}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Główna sekcja z produktami */}
        <motion.div
          key={activeTag} // Zmiana klucza wymusza pełną re-animację przy zmianie kategorii
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="mb-8 md:mb-12"
        >
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loading />
            </div>
          ) : recommended.length === 0 ? (
            <motion.div
              variants={itemVariants}
              className={`bg-gray-50 rounded-xl p-8 text-center border ${currentTagColors.border}`}
            >
              <div
                className={`mx-auto h-20 w-20 flex items-center justify-center rounded-full ${currentTagColors.bg} ${currentTagColors.text}`}
              >
                {tagIcons[activeTag]}
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-800">
                W tej chwili nie mamy produktów w tej kategorii
              </h3>
              <p className="mt-2 text-gray-500 max-w-md mx-auto">
                Zapraszamy do sprawdzenia innych kategorii lub powrotu wkrótce -
                regularnie uzupełniamy nasz asortyment
              </p>
            </motion.div>
          ) : (
            <>
              {/* Desktop view - grid z animacjami */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {recommended.slice(0, 8).map((product, index) => (
                  <motion.div
                    key={product.id}
                    variants={itemVariants}
                    className="h-full flex"
                    whileHover={{
                      y: -5,
                      transition: { duration: 0.2 },
                    }}
                  >
                    <ProductCard
                      product={product}
                      vertical={true}
                      className={`w-full flex-1 flex flex-col rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg ${currentTagColors.ring} hover:ring-2 hover:ring-opacity-50`}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Mobile view - ulepszona karuzela */}
              <div className="md:hidden relative">
                <Swiper
                  spaceBetween={12}
                  slidesPerView={1.2}
                  centeredSlides={false}
                  pagination={{
                    clickable: true,
                    dynamicBullets: true,
                  }}
                  navigation={true}
                  modules={[Pagination, Navigation, Autoplay]}
                  autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }}
                  className={`recommendedSwiper pb-12 pt-2 px-2 -mx-2`}
                >
                  {recommended.map((product) => (
                    <SwiperSlide key={product.id} className="h-full">
                      <div className="py-2 h-full">
                        <ProductCard
                          product={product}
                          vertical={true}
                          className={`h-full flex flex-col rounded-xl overflow-hidden shadow-sm ${currentTagColors.ring} ring-1 ring-opacity-50`}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </>
          )}

          {/* Przycisk "Zobacz wszystkie" */}
          {!isLoading && recommended.length > 0 && (
            <motion.div
              variants={fadeInVariants}
              className="text-center mt-10 md:mt-12"
            >
              <Link to={`/szukaj?tag=${activeTag}`}>
                <motion.button
                  className={`inline-flex items-center gap-2 px-6 py-3 ${currentTagColors.bg} ${currentTagColors.text} rounded-full font-medium transition-all ${currentTagColors.hover}`}
                  whileHover={{
                    scale: 1.05,
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>
                    Zobacz wszystkie {tagDetails[activeTag].label.toLowerCase()}{' '}
                    produkty
                  </span>
                  <KeyboardArrowRight />
                </motion.button>
              </Link>
            </motion.div>
          )}
        </motion.div>

        {/* Przebudowana sekcja szybkich linków */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="mt-16"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {Object.values(Tag).map((tag) => {
              const tagColor = tagColorClasses[tagDetails[tag].color];
              const isActive = activeTag === tag;

              return (
                <motion.div
                  key={tag}
                  variants={itemVariants}
                  whileHover={{
                    scale: 1.03,
                    boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.15)',
                  }}
                  whileTap={{ scale: 0.98 }}
                  className={`bg-white border ${tagColor.border} rounded-xl overflow-hidden shadow-sm transition-all duration-300 group`}
                >
                  <Link to={`/szukaj?tag=${tag}`} className="block p-5">
                    <div className="flex items-center space-x-4 mb-4">
                      <div
                        className={`p-3 rounded-full ${tagColor.bg} ${tagColor.text} transition-transform group-hover:scale-110 duration-300`}
                      >
                        {tagIcons[tag]}
                      </div>
                      <div>
                        <h4
                          className={`font-bold text-lg ${isActive ? tagColor.text : 'text-gray-800'}`}
                        >
                          {tagDetails[tag].label}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {tagDetails[tag].description}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        {recommended.filter((p) => p.tag === tag).length}{' '}
                        produktów
                      </span>
                      <span
                        className={`flex items-center text-sm font-medium ${tagColor.text} transform group-hover:translate-x-1 transition-transform`}
                      >
                        <span>Przejdź</span>
                        <KeyboardArrowRight fontSize="small" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default Recommended;
